import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled from 'styled-components';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { FlexBoxCol as Col } from '@r1-frontend/ui-react/components/layouts/flexBoxGrid';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

import { TCustomStyle } from '~/src/features/index/Banners/PromoBannersSection/PromoBanner/index';
import defaultImageFallback from '~/static/images/defaultImageFallback.svg';

export const Wrapper = styled.div<{ backgroundImage: string, extStyles?: TCustomStyle }>`
    height: 320px;
    background-image: url(${defaultImageFallback});
    background: url(${props => props.backgroundImage}) center / cover no-repeat;
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.laptop}) {
        height: 280px;
        border-radius: 0;
        ${p => p.extStyles?.laptop ? p.extStyles?.laptop : ''};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        height: auto;
        border-radius: 24px;
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const DescCol = styled(Col)<{ extStyles?: TCustomStyle }>`
    display: flex;
    align-items: center;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${p => p.extStyles?.desktop ? p.extStyles?.desktop : ''};
    }

    @media (max-width: ${wideBreakpoints.laptop}) {
        ${p => p.extStyles?.laptop ? p.extStyles.laptop : ''};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        max-width: 100% !important;
        flex-basis: 100% !important;
        align-items: flex-start;
        height: 220px;
        background: ${COLORS.BgSurface};
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }

    @media (max-width: ${wideBreakpoints.sMobile}) {
        height: auto;
        ${p => p.extStyles?.sMobile ? p.extStyles?.sMobile : ''};
    }
`;

export const Desc = styled.div<{ extStyles?: TCustomStyle }>`
    display: block;
    width: 100%;
    padding-left: 40px;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.mobile}) {
        padding: 16px 20px;
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const BannerLabel = styled.p<{ textColor?: string }>`
    ${FONTS.LABEL};
    margin-bottom: 4px;
    color: ${props => props.textColor || COLORS.TextHint};

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.LABEL_S};
    }
`;

export const Title = styled.p<{ textColor: string, extStyles?: TCustomStyle }>`
    ${FONTS.H2};
    color: ${props => props.textColor};
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.laptop}) {
        ${FONTS.H2};
        ${p => p.extStyles?.laptop ? p.extStyles?.laptop : ''};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${p => p.extStyles?.tablet ? p.extStyles?.tablet : ''};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${FONTS.H4};
        color: ${COLORS.TextPrimary};
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const Text = styled.p<{ textColor: string, extStyles?: TCustomStyle }>`
    ${FONTS.M};
    line-height: 28px;
    color: ${props => props.textColor};
    margin-top: 8px;
    margin-bottom: 24px;
    align-self: flex-start;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};
    
    @media (max-width: ${wideBreakpoints.laptop}) {
        ${FONTS.XS};
        margin-bottom: 16px;
        ${p => p.extStyles?.laptop ? p.extStyles?.laptop : ''};
    }

    @media (max-width: ${wideBreakpoints.tablet}) {
        ${p => p.extStyles?.tablet ? p.extStyles?.tablet : ''};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        color: ${COLORS.TextSecondary};
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const SButton = styled(Button)<{ extStyles?: TCustomStyle }>`
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.mobile}) {
        width: 100%;
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const ImageCol = styled(Col)<{ extStyles?: TCustomStyle }>`
    position: relative;
    height: 100%;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.sDesktop}) {
        ${p => p.extStyles?.desktop ? p.extStyles?.desktop : ''};
    }

    @media (max-width: ${wideBreakpoints.laptop}) {
        ${p => p.extStyles?.laptop ? p.extStyles.laptop : ''};
    }

    @media (max-width: ${wideBreakpoints.mobile}) {
        max-width: 100% !important;
        flex-basis: 100% !important;
        height: 200px;
        order: -1;
        background-repeat: no-repeat;
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;

export const Image = styled(LazyLoadImage)<{ $extStyles?: TCustomStyle }>`
    position: absolute;
    margin: auto;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    max-height: 100%;
    ${p => p.$extStyles?.default ? p.$extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.mobile}) {
        ${p => p.$extStyles?.mobile || ''}
    }
`;

export const Legal = styled.div<{ textColor?: string }>`
    position: absolute;
    bottom: 4px;
    left: 4px;
    color: ${p => p.textColor ?? COLORS.BgMain};

    @media (max-width: ${wideBreakpoints.mobile}) {
        max-width: 235px;
    }
`;

export const LegalTitle = styled.div`
    ${FONTS.S};
`;

export const LegalDescription = styled.div`
    ${FONTS.XXXS};
    margin-right: 128px;

    @media (max-width: ${wideBreakpoints.mobile}) {
        margin-right: 0;
    }
`;

export const AdvertisingBlock = styled.div<{ extStyles?: TCustomStyle }>`
    position: absolute;
    bottom: 24px;
    right: 24px;
    ${p => p.extStyles?.default ? p.extStyles?.default : ''};

    @media (max-width: ${wideBreakpoints.mobile}) {
        bottom: 16px;
        right: 16px;
        ${p => p.extStyles?.mobile ? p.extStyles?.mobile : ''};
    }
`;
